/**
 * RiskLo Tally 2: sign a core release in the browser, so an admin can publish
 * from any computer (e.g. the Windows PC that built the DLL) with no other tools.
 *
 * The private key file is read locally and used with the browser's Web Crypto;
 * it's never uploaded. The canonical string must match the server's exactly
 * (server/services/tallyCoreManifest.js) and the loader's (C#).
 */

const FORMAT = 'risklo-tally-core-manifest-v1';

export function canonicalString(m) {
  const bool = (v) => (v === true ? 'true' : 'false');
  return [
    FORMAT,
    `build=${m.build}`,
    `seq=${m.seq}`,
    `version=${m.version}`,
    `sha256=${String(m.sha256 || '').toLowerCase()}`,
    `size=${m.size}`,
    `url=${m.url}`,
    `mandatory=${bool(m.mandatory)}`,
    `disabled=${bool(m.disabled)}`,
    `stop_now=${bool(m.stop_now)}`,
    `min_loader=${m.min_loader || ''}`,
    `min_nt=${m.min_nt || ''}`,
    `key_id=${m.key_id}`,
  ].join('\n');
}

const toHex = (buf) => Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');

function toBase64(buf) {
  let s = '';
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

/** The build a DLL is for, from its name (RiskLoTallyCore_SelfUse.dll / _CSA.dll). */
export function buildFromFileName(name) {
  if (/_csa\.dll$/i.test(name || '')) return 'csa';
  if (/_selfuse\.dll$/i.test(name || '')) return 'selfuse';
  return null;
}

async function importPrivateKey(pemText) {
  const m = /-----BEGIN PRIVATE KEY-----([\s\S]+?)-----END PRIVATE KEY-----/.exec(pemText || '');
  if (!m) throw new Error('That isn’t a signing key file (selfuse-1.pem or csa-1.pem).');
  const der = Uint8Array.from(atob(m[1].replace(/\s+/g, '')), (c) => c.charCodeAt(0));
  return window.crypto.subtle.importKey('pkcs8', der, { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' }, false, ['sign']);
}

/**
 * Build and sign the manifest. dllBytes: ArrayBuffer; keyPem: the key file's text.
 * Returns { manifestText, sha256 }.
 */
export async function signRelease({ dllBytes, keyPem, build, version, mandatory = true, disabled = false, stopNow = false, minLoader = '2.0.0' }) {
  if (!window.crypto?.subtle) throw new Error('This browser can’t sign (it needs https or localhost).');
  const sha256 = toHex(await window.crypto.subtle.digest('SHA-256', dllBytes));
  const fields = {
    build,
    seq: Math.floor(Date.now() / 1000),
    version,
    sha256,
    size: dllBytes.byteLength,
    url: `releases/${sha256}.dll`,
    mandatory: Boolean(mandatory),
    disabled: Boolean(disabled),
    stop_now: Boolean(stopNow),
    min_loader: minLoader,
    min_nt: '',
    key_id: `${build}-1`,
  };
  const key = await importPrivateKey(keyPem);
  const sig = await window.crypto.subtle.sign('RSASSA-PKCS1-v1_5', key, new TextEncoder().encode(canonicalString(fields)));
  const manifest = { ...fields, signature: toBase64(sig), signed_at: new Date().toISOString() };
  return { manifestText: JSON.stringify(manifest, null, 2), sha256 };
}
